'use client';

export enum StorageMode {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
  BACKUP = 'backupStorage',
}

// A wrapper to protect against SecurityError
// In browsers such as Safari and Firefox localStorage is blocked and throw SecurityError
// Only Chrome Incognito supports accessing localStorage since it resets localStorage within each sesssion.
// https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage#securityerror
// https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage#securityerror
export class BrowserStorageWrapper {
  #storageMode: StorageMode | undefined;
  backupStorage: { [key: string]: string };

  constructor() {
    this.backupStorage = {};
  }

  getStorageMode() {
    if (this.#storageMode) {
      return this.#storageMode;
    }
    try {
      localStorage.length;
      this.#storageMode = StorageMode.LOCAL;
    } catch {
      // this means localStorage isn't accessible
      try {
        sessionStorage.length;
        this.#storageMode = StorageMode.SESSION;
      } catch {
        // this means sessionStorage isn't accessible
        this.#storageMode = StorageMode.BACKUP;
      }
    }
    return this.#storageMode;
  }

  setItem(key: string, value: string) {
    const storageMode = this.getStorageMode();
    switch (storageMode) {
      case StorageMode.LOCAL:
        localStorage.setItem(key, value);
        break;
      case StorageMode.SESSION:
        sessionStorage.setItem(key, value);
        break;
      case StorageMode.BACKUP:
        this.backupStorage[key] = value;
        break;
    }
  }

  getItem(key: string): string | null {
    const storageMode = this.getStorageMode();
    switch (storageMode) {
      case StorageMode.LOCAL:
        return localStorage.getItem(key);
      case StorageMode.SESSION:
        return sessionStorage.getItem(key);
      case StorageMode.BACKUP:
        return this.backupStorage[key] || null;
    }
    return null;
  }
}
